import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { REQUERIMENTO_ID_KEY } from '../../core/constants/storage-keys';
import { Home, Requerimento } from '../../core/models/requerimento';
import { LocalStorageService } from './local-storage-service';
import { RequerimentoService } from './requerimento-service';

@Injectable({
  providedIn: 'root',
})
export class RequerimentoState {
  private readonly localStorageService = inject(LocalStorageService);
  private readonly requerimentoService = inject(RequerimentoService);
  private readonly requerimentoAtual = signal<Requerimento | null>(null);

  /** GET /v1/requerimentos/home (participante, elegibilidade...): guardada pelo resolver da home. */
  private readonly homeAtual = signal<Home | null>(null);

  readonly requerimento = this.requerimentoAtual.asReadonly();

  definirHome(home: Home): void {
    this.homeAtual.set(home);
  }

  /** A home guardada ou, ao recarregar uma etapa sem passar pela home, a buscada de novo na API. */
  async carregarHome(): Promise<Home> {
    const atual = this.homeAtual();
    if (atual) return atual;

    const home = await this.requerimentoService.getHome();
    this.homeAtual.set(home);
    return home;
  }

  definir(requerimento: Requerimento): void {
    this.requerimentoAtual.set(requerimento);
    this.localStorageService.add(REQUERIMENTO_ID_KEY, String(requerimento.id));
  }

  async carregar(): Promise<Requerimento> {
    const atual = this.requerimentoAtual();
    if (atual) return atual;

    const id = Number(this.localStorageService.get(REQUERIMENTO_ID_KEY));
    if (!id) {
      throw new HttpErrorResponse({ status: 404, statusText: 'No requerimento in progress.' });
    }

    try {
      const requerimento = await this.requerimentoService.getPorId(id);
      this.definir(requerimento);
      return requerimento;
    } catch (erro) {
      if (erro instanceof HttpErrorResponse && erro.status === 404) this.limpar();
      throw erro;
    }
  }

  limpar(): void {
    this.requerimentoAtual.set(null);
    this.localStorageService.removeItem(REQUERIMENTO_ID_KEY);
  }
}
